/**
 * Client-safe map of content routes that have a plain-Markdown mirror at
 * `/md/<path>` (served by src/routes/md.$.ts). Used both by the server
 * generator and by the document head to advertise the mirror to crawlers.
 */
export type MarkdownLang = "en" | "es" | "zh-cn";

export interface MarkdownRoute {
  prefix: string;
  table: string;
  lang: MarkdownLang;
  label: string;
}

/** URL prefix → table. Longest prefix wins so hubs never match an article. */
export const MARKDOWN_ROUTES: MarkdownRoute[] = [
  // Blog (per-language rows, `lang` column)
  { prefix: "/en/blog/", table: "blog_posts", lang: "en", label: "Blog post" },
  { prefix: "/es/blog/", table: "blog_posts", lang: "es", label: "Artículo del blog" },
  { prefix: "/zh-cn/blog/", table: "blog_posts", lang: "zh-cn", label: "博客文章" },
  // Regulatory updates
  { prefix: "/en/regulatory-updates/", table: "regulatory_updates", lang: "en", label: "Regulatory update" },
  { prefix: "/es/actualizaciones-regulatorias/", table: "regulatory_updates", lang: "es", label: "Actualización regulatoria" },
  { prefix: "/zh-cn/regulatory-updates/", table: "regulatory_updates", lang: "zh-cn", label: "Regulatory update" },
  // Case studies
  { prefix: "/en/case-studies/", table: "case_studies", lang: "en", label: "Case study" },
  { prefix: "/en/case-study/", table: "case_studies", lang: "en", label: "Case study" },
  { prefix: "/es/casos-de-exito/", table: "case_studies", lang: "es", label: "Caso de éxito" },
  { prefix: "/es/casos-de-estudio/", table: "case_studies", lang: "es", label: "Caso de éxito" },
  { prefix: "/es/casos-clientes/", table: "case_studies", lang: "es", label: "Caso de éxito" },
  { prefix: "/es/caso-estudio/", table: "case_studies", lang: "es", label: "Caso de éxito" },
  { prefix: "/zh-cn/case-studies/", table: "case_studies", lang: "zh-cn", label: "Case study" },
  // White papers / downloads
  { prefix: "/en/white-papers/", table: "white_papers", lang: "en", label: "White paper" },
  // Checklists
  { prefix: "/en/downloads/checklists/", table: "checklists", lang: "en", label: "Checklist" },
  { prefix: "/es/downloads/checklists/", table: "checklists", lang: "es", label: "Checklist" },
  { prefix: "/zh-cn/downloads/checklists/", table: "checklists", lang: "zh-cn", label: "Checklist" },
  { prefix: "/en/whitepapers/", table: "white_papers", lang: "en", label: "White paper" },
  { prefix: "/en/downloads/", table: "white_papers", lang: "en", label: "White paper" },
  { prefix: "/es/whitepapers/", table: "white_papers", lang: "es", label: "White paper" },
  { prefix: "/es/downloads/", table: "white_papers", lang: "es", label: "White paper" },
  { prefix: "/zh-cn/whitepapers/", table: "white_papers", lang: "zh-cn", label: "White paper" },
  { prefix: "/zh-cn/downloads/", table: "white_papers", lang: "zh-cn", label: "White paper" },

  // Company news
  { prefix: "/en/news/", table: "company_news", lang: "en", label: "Company news" },
  { prefix: "/es/noticias/", table: "company_news", lang: "es", label: "Noticias" },
  { prefix: "/zh-cn/news/", table: "company_news", lang: "zh-cn", label: "Company news" },
  // Webinars
  { prefix: "/en/webinars/", table: "webinars", lang: "en", label: "Webinar" },
  { prefix: "/es/webinars/", table: "webinars", lang: "es", label: "Webinar" },
  { prefix: "/es/webinar-on-demand/", table: "webinars", lang: "es", label: "Webinar" },
  { prefix: "/zh-cn/webinars/", table: "webinars", lang: "zh-cn", label: "Webinar" },
  // Events
  { prefix: "/en/events/", table: "events", lang: "en", label: "Event" },
  { prefix: "/es/eventos/", table: "events", lang: "es", label: "Evento" },
];

export interface MarkdownTarget extends MarkdownRoute {
  slug: string;
  /** The HTML page path (no `.md`). */
  path: string;
}

/** Resolves an article page path (with or without a trailing `.md`). */
export function resolveMarkdownTarget(rawPath: string): MarkdownTarget | null {
  let path: string;
  try {
    path = decodeURIComponent(rawPath);
  } catch {
    path = rawPath;
  }
  path = path.replace(/\/+$/, "");
  const pagePath = path.endsWith(".md") ? path.slice(0, -3) : path;
  const match = MARKDOWN_ROUTES.filter((entry) => pagePath.startsWith(entry.prefix)).sort(
    (a, b) => b.prefix.length - a.prefix.length,
  )[0];
  if (!match) return null;
  const slug = pagePath.slice(match.prefix.length);
  // Only single-segment slugs are articles; nested paths are hubs/filters.
  if (!slug || slug.includes("/")) return null;
  return { ...match, slug, path: pagePath };
}

/** Paths that are not public content and therefore have no Markdown mirror. */
const MIRROR_EXCLUDED = [
  "/md/",
  "/api/",
  "/admin",
  "/auth",
  "/lovable",
  "/sitemap",
  "/robots",
  "/llms",
  "/not-found",
  "/search",
  "/thank-you",
  "/gracias",
];

/**
 * `/md/...` mirror path for a page, or null when it has none.
 *
 * Database articles resolve through MARKDOWN_ROUTES; every other public page is
 * mirrored by re-rendering its own SSR HTML as Markdown (see
 * src/lib/staticMarkdown.server.ts), so all content pages advertise a mirror.
 */
export function markdownMirrorPath(pathname: string): string | null {
  const target = resolveMarkdownTarget(pathname);
  if (target) return `/md${target.path}`;
  const path = pathname.replace(/\/+$/, "");
  if (!path || path === "/") return null;
  if (!/^\/(en|es|zh-cn)(\/|$)/.test(path)) return null;
  const rest = path.replace(/^\/(en|es|zh-cn)/, "") || "/";
  if (MIRROR_EXCLUDED.some((prefix) => rest.startsWith(prefix) || path.startsWith(prefix))) {
    return null;
  }
  return `/md${path}`;
}
